import { Component, OnDestroy } from '@angular/core';
import { Router, NavigationStart, ActivationEnd } from '@angular/router';

import { environment } from '@cp/http';

import { AppConfigService } from '@library/app.config';
import { BrandingService } from '@library/common/branding';
import {
   BreakpointService,
   BrowserCheckService,
   EnvironmentService,
   ErrorMessageService,
   GoogleAnalyticsService,
   PrefetchService,
   TextService,
   MaintenanceService,
   PortalService,
} from '@library/core/services';

import * as FullStory from '@fullstory/browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-root',
   template: `
   <router-outlet></router-outlet>
   <ng-container *ngIf="!loaded">
      <div style="height: 100vh; background-color: #F2F2F2;">
         <div class="loading">
            <style>
               body { background-color: #F2F2F2; }
               path {
                  stroke: #979797;
                  stroke-dasharray: 1;
                  stroke-dashoffset: 1;
                  animation: dash 12s linear infinite;
               }
               svg g:nth-child(2) path { animation-delay: 3s; }
               svg g:nth-child(3) path { animation-delay: 6s; }
               svg g:nth-child(4) path { animation-delay: 9s; }
               @keyframes dash {
                  0% { opacity: 0; stroke-dashoffset: 1; }
                  1% { opacity: 1; }
                  24% { opacity: 1; }
                  25% { opacity: 0; stroke-dashoffset: 3; }
                  100% { opacity: 0; }
               }
            </style>
            <svg id="Loading" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 200 200" class="common-index">
               <g>
                  <path id="Truck" d="M270.026415,84.878709L373.345703,84.878709L373.345703,153.973981L355.451700,153.973981C355.451700,166.730910,350.185996,173.109375,339.654587,173.109375C329.123178,173.109375,323.732245,166.730910,323.481789,153.973981L270.026415,153.973981L270.026415,159.023258L261.118207,159.023258C261.118207,168.414003,256.679417,173.109375,247.801835,173.109375C238.924253,173.109375,234.003607,168.414003,233.039895,159.023258L213,159.023258L213,153.973981L218.233036,153.973981L218.233036,128.994042L233.039895,128.994042C234.702813,112.113124,237.559238,102.280338,241.609171,99.495684C245.659103,96.711031,255.131518,95.926994,270.026415,97.143574L270.026415,84.878709Z" transform="matrix(-1 0 0 1 393.17285162999980 -28.99404215499960)" fill="none" stroke-width="3" pathLength="1"></path>
               </g>
               <g>
                  <path id="Airplane" d="M252.441540,148.393898L287.593131,112.959956L226.851078,78.256677L237.420911,65.887128L308.823322,86.490112L339.761205,61.561342C348.509049,57.941792,354.732979,57.941792,358.432996,61.561342C362.133012,65.180892,362.133012,70.746004,358.432996,78.256677L333.381371,108.591063L354.247307,182.909661L343.196832,193.166968L308.823322,132.401206L271.895723,170.675899L271.895723,193.166968L259.047686,207L244.948579,173.963713L214.227198,161.750597L226.851078,148.393898L252.441540,148.393898Z" transform="matrix(1 0 0 1 -188.21760299999988 -32.18157803999995)" fill="none" stroke-width="3" pathLength="1"></path>
               </g>
               <g transform="matrix(1 0 0 1 10.69531211000000 59.50000059500006)">
                  <path id="CargoShip" d="M44.210938,41.352261L117.062500,41.960938L117.062500,41.960938C119.255423,38.632967,121.462454,36.479321,123.683594,35.500000C125.904734,34.520679,129.041452,34.318215,133.093750,34.892609L178.978516,34.892609L154.921875,78.699219C153.309973,80.236419,151.875729,81.176523,150.619141,81.519531C149.362553,81.862539,147.352787,81.862539,144.589844,81.519531L30.537109,81.519531C28.099488,81.777203,26.517457,81.777203,25.791016,81.519531C25.064575,81.261860,23.996215,80.321756,22.585938,78.699219L0.369141,41.960938L6.013672,41.960938L6.013672,0.927734L44.210938,0.927734Z" transform="matrix(1 0 0 1 -0.36914063000000 -0.85226086500001)" fill="none" stroke-width="3" pathLength="1"></path>
               </g>
               <g>
                  <path id="Train" d="M370.997492,107.511549L370.997492,146.206181L363.292146,146.206181C362.429180,150.836302,359.654262,153.151363,354.967393,153.151363C350.280523,153.151363,347.505606,150.836302,346.642640,146.206181L329.993134,146.206181C329.144557,150.836302,326.369640,153.151363,321.668381,153.151363C316.967122,153.151363,314.192205,150.836302,313.343628,146.206181L305.409426,146.206181L305.409426,149.524336L252.532169,149.524336L252.532169,146.206181L246.671691,146.206181C246.129852,150.836302,243.379572,153.151363,238.420852,153.151363C233.462131,153.151363,230.687213,150.836302,230.096099,146.206181L213.446593,146.206181C212.838427,150.836302,210.063510,153.151363,205.121840,153.151363C200.180170,153.151363,197.405253,150.836302,196.797087,146.206181L193.311263,146.206181L189.621002,151.999372L180.880952,151.999372L184.554189,142.745346L182.924702,139.126314L185.848861,114.251213C187.009137,111.029550,188.266517,109.105782,189.621002,108.479910C190.975487,107.854038,194.805456,107.427075,201.110911,107.199020C202.888930,103.618651,204.507067,101.292241,205.965323,100.219790C207.423579,99.147338,209.613253,98.673355,212.534345,98.797839L238.420852,98.797839L238.420852,96.646802L246.671691,96.646802L246.671691,98.797839L280.170990,98.797839L280.170990,96.646802L288.369369,96.646802L288.369369,98.797839L321.668381,98.797839L321.668381,96.646802L329.993134,96.646802L329.993134,98.797839L361.514418,98.797839C365.683972,98.736800,368.317796,99.210784,369.415891,100.219790C370.513985,101.228795,371.041186,103.659382,370.997492,107.511549Z" transform="matrix(-1 0 0 1 375.44047606000004 -24.15732085000018)" fill="none" stroke-width="3" pathLength="1"></path>
               </g>
            </svg>
         </div>
      </div>
   </ng-container>
   `,
})
export class AppComponent implements OnDestroy {

   sub$: Subscription = new Subscription();

   loaded = false;

   constructor(
      public router: Router,
      public brand: BrandingService,
      private error: ErrorMessageService,
      private e: EnvironmentService,
      private gtag: GoogleAnalyticsService,
      private appConfig: AppConfigService,
      private check: BrowserCheckService,
      private breakpoints: BreakpointService,
      private text: TextService,
      private prefetch: PrefetchService,
      private maintenance: MaintenanceService,
      private portalService: PortalService,
   ) {
      this.portalService.current = '4pl';

      this.e.environment = environment;
      this.sub$.add(this.gtag.load( environment?.gtagId ));
      this.breakpoints.tap();
      this.check.check();
      this.sub$.add(this.maintenance.check());
      FullStory.init({ orgId: 'o-1MB0F2-na1', devMode: !environment.production });

      // console.log( 'requesting server', this.server );

      this.appConfig.load();

      this.text.loginTitle = `Ship and Track with Our 4PL Portal`;
      this.text.loginSubTitle = `Designed to bring business value to you,
         Our 4PL Portal will provide access to vital information on all your shipments.
         Log in today to book or track your freight.`;

      this.prefetch.items = [
         'servicelevels',
         'currencies',
         'countries',
      ];

      const whitelist = [
         '/error',
         '/termsagree',
      ];

      this.router.events
      .pipe(
         filter((event) => event instanceof NavigationStart),
         filter((event: NavigationStart) => !whitelist.includes( event.url )),
         untilDestroyed(this),
      )
      .subscribe(_ => {
         this.error.resetErrors();
      });

      this.router.events
      .pipe(
         filter((event) => event instanceof ActivationEnd),
         untilDestroyed(this),
      )
      .subscribe(_ => {
         this.loaded = true;
      });
   }

   ngOnDestroy(): void {
      this.sub$.unsubscribe();
   }
}